/**
 * Static geometry for NYISO's tracked interconnection cables/interfaces
 * ("import cables"). No server feed provides route geometry — these paths are
 * researched/verified by hand and must be transcribed exactly, not "fixed" or
 * simplified. `id` matches the flow id the server emits in `GridSummary.flows`
 * (see packages/server/src/feeds/grid/normalize.ts TRACKED_INTERFACES).
 */

import type { LngLatBoundsTuple } from '../map/featureBounds.js';

/** One cable's static route + display metadata. */
export interface CableDef {
  /** Matches `GridFlowEntry.id` from the server summary. */
  id: string;
  /** Full display name. */
  name: string;
  /** Short label for compact UI (tooltip title, chip). */
  shortName: string;
  /** Rated transfer capacity in MW, used to compute utilization for line width. */
  capacityMw: number;
  /** Where the cable comes ashore / interconnects into the NY grid. */
  landing: string;
  /** Where the cable originates. */
  origin: string;
  /**
   * Name of the real facility at the remote (path-start) end, when it sits on
   * the map. Absent for cables whose path starts at a map-edge truncation
   * (CHPE continues to Québec, CSC to New Haven) — those get no start bullet.
   */
  remoteTerminus?: string;
  /** Route as [lng, lat] points, origin-to-landing order. */
  path: [number, number][];
}

export const CABLES: CableDef[] = [
  {
    id: 'chpe',
    name: 'Champlain Hudson Power Express',
    shortName: 'CHPE',
    capacityMw: 1250,
    landing: 'Astoria Converter Station, Queens',
    origin: 'Hydro-Québec',
    path: [
      [-73.893, 41.05], [-73.896, 41.0], [-73.905, 40.97], [-73.914, 40.94],
      [-73.925, 40.91], [-73.928, 40.885], [-73.9245, 40.879], [-73.917, 40.877],
      [-73.9108, 40.8738], [-73.914, 40.863], [-73.924, 40.849], [-73.9325, 40.833],
      [-73.9335, 40.818], [-73.929, 40.807], [-73.9245, 40.8022], [-73.915, 40.8008],
      [-73.906, 40.7995], [-73.902, 40.793], [-73.899, 40.788], [-73.8975, 40.7845],
      [-73.9, 40.782],
    ],
  },
  {
    id: 'htp',
    name: 'Hudson Transmission (HTP)',
    shortName: 'HTP',
    capacityMw: 660,
    landing: 'W 49th St Substation, Manhattan',
    origin: 'PJM (Ridgefield NJ)',
    remoteTerminus: 'Bergen Converter Station, Ridgefield NJ',
    path: [
      [-74.0237, 40.837], [-74.018, 40.83], [-74.008, 40.823], [-73.99, 40.818],
      [-73.977, 40.8145], [-73.985, 40.801], [-73.993, 40.788], [-74.0, 40.776],
      [-73.9975, 40.7705], [-73.9935, 40.765],
    ],
  },
  {
    id: 'vft',
    name: 'Linden VFT',
    shortName: 'VFT',
    capacityMw: 315,
    landing: 'Goethals Substation, Staten Island',
    origin: 'PJM (Linden NJ)',
    remoteTerminus: 'Linden Cogeneration Plant, Linden NJ',
    path: [
      [-74.2112, 40.6239], [-74.209, 40.622], [-74.206, 40.62], [-74.2045, 40.6188],
      [-74.2025, 40.618], [-74.2, 40.6173], [-74.198, 40.6172], [-74.1963, 40.6171],
    ],
  },
  {
    id: 'neptune',
    name: 'Neptune Cable',
    shortName: 'Neptune',
    capacityMw: 660,
    landing: 'Duffy Ave Converter, New Cassel LI',
    origin: 'PJM (Sayreville NJ)',
    remoteTerminus: 'Sayreville Converter Station, NJ',
    path: [
      [-74.3531, 40.4737], [-74.35, 40.48], [-74.32, 40.4855], [-74.29, 40.4875],
      [-74.265, 40.49], [-74.22, 40.487], [-74.15, 40.48], [-74.08, 40.476],
      [-74.015, 40.484], [-73.94, 40.495], [-73.82, 40.515], [-73.68, 40.54],
      [-73.57, 40.565], [-73.529, 40.582], [-73.515, 40.589], [-73.5125, 40.6],
      [-73.509, 40.63], [-73.508, 40.656], [-73.513, 40.69], [-73.517, 40.725],
      [-73.526, 40.743], [-73.533, 40.753], [-73.542, 40.758], [-73.5511, 40.7606],
    ],
  },
  {
    id: 'csc',
    name: 'Cross Sound Cable',
    shortName: 'CSC',
    capacityMw: 330,
    landing: 'Shoreham Converter, Brookhaven LI',
    origin: 'ISO-NE (New Haven CT)',
    path: [
      [-72.882, 41.1], [-72.8795, 41.07], [-72.877, 41.04], [-72.8745, 41.01],
      [-72.8715, 40.99], [-72.869, 40.975], [-72.8665, 40.964], [-72.865, 40.9608],
    ],
  },
];

/**
 * Bounding box over every cable's path. Used as the static focus-fallback
 * for the 'grid_load' layer, which has no server-provided features for
 * `featuresToBounds` to compute a camera fit from.
 */
export const CABLE_BOUNDS: LngLatBoundsTuple = computeCableBounds(CABLES);

function computeCableBounds(cables: readonly CableDef[]): LngLatBoundsTuple {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const cable of cables) {
    for (const [lng, lat] of cable.path) {
      if (lng < minLng) minLng = lng;
      if (lat < minLat) minLat = lat;
      if (lng > maxLng) maxLng = lng;
      if (lat > maxLat) maxLat = lat;
    }
  }
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}
